import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRocket,
  faPuzzlePiece,
  faLaptopCode,
  faArrowUpRightFromSquare,
  faPersonChalkboard,
  faTriangleExclamation,
  faFolderOpen,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import ParentShowcase from './ParentShowcase';
import './Curriculum.css';

const API = process.env.REACT_APP_API_BASE_URL;
const DEFAULT_IMG = '/assets/coursesimages/206071006_a1d57977-9a9b-4428-9a06-4027494bf664-1024x576.jpg';

const TABS = [
  { key: 'demo', label: 'Demo Sessions', icon: faRocket },
  { key: 'little', label: 'Little Course', sub: 'Grade 1–3', icon: faPuzzlePiece, plan: 1, type: 'Little Course (Grade 1-3)' },
  { key: 'junior', label: 'Junior Course', sub: 'Grade 4–12', icon: faLaptopCode, plan: 2, type: 'Junior Course (Grade 4-12)' },
];

const onImgError = (e) => {
  if (!e.currentTarget.src.endsWith(DEFAULT_IMG)) e.currentTarget.src = DEFAULT_IMG;
};

const Curriculum = ({ userData }) => {
  const [params, setParams] = useSearchParams();
  const tabKey = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'demo';
  const tab = TABS.find((t) => t.key === tabKey);

  const [demo, setDemo] = useState(null); // { little: [], junior: [] }
  const [modules, setModules] = useState({}); // plan -> [{id, name}]
  const [activeModule, setActiveModule] = useState({}); // plan -> moduleId
  const [courses, setCourses] = useState({}); // moduleId -> []
  const [loading, setLoading] = useState({});
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const latestModule = useRef({});

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'demo') next.delete('tab');
    else next.set('tab', key);
    setParams(next, { replace: true });
    setError('');
  };

  const busy = (key, on) => setLoading((prev) => ({ ...prev, [key]: on }));

  // ---------- Demo sessions (same endpoint, loaded once) ----------
  useEffect(() => {
    if (tabKey !== 'demo' || demo) return;
    (async () => {
      busy('demo', true);
      try {
        const res = await axios.get(`${API}/api/teacher/Curriculum/demo`);
        const map = (item) => ({
          id: item.Id,
          name: item.ProjectName,
          image: item.ImgLink,
          details: item.ProjectDetails,
          link: item.ProjectLink,
          ageRange: item.AgeDetails,
        });
        const list = Array.isArray(res.data) ? res.data : [];
        setDemo({
          little: list.filter((i) => i.Plan === 1).map(map),
          junior: list.filter((i) => i.Plan === 2).map(map),
        });
      } catch (err) {
        console.error('Error fetching demo sessions:', err);
        setError('We couldn’t load the demo sessions.');
      } finally {
        busy('demo', false);
      }
    })();
  }, [tabKey, demo, retry]);

  // ---------- Modules of a plan (same endpoint, loaded once per plan) ----------
  useEffect(() => {
    if (!tab.plan || modules[tab.plan]) return;
    const plan = tab.plan;
    (async () => {
      busy(`m${plan}`, true);
      try {
        const res = await axios.get(`${API}/api/teacher/Curriculum/modules/${plan}`);
        const list = (Array.isArray(res.data) ? res.data : []).map((m) => ({ id: String(m.Id), name: `${m.ModuleName}` }));
        setModules((prev) => ({ ...prev, [plan]: list }));
        // open the first module straight away
        setActiveModule((prev) => (prev[plan] || !list.length ? prev : { ...prev, [plan]: list[0].id }));
      } catch (err) {
        console.error('Error fetching modules:', err);
        setError('We couldn’t load the modules.');
      } finally {
        busy(`m${plan}`, false);
      }
    })();
  }, [tab.plan, modules, retry]);

  // ---------- Courses of the selected module (same endpoint, cached) ----------
  const moduleId = tab.plan ? activeModule[tab.plan] : null;
  useEffect(() => {
    if (!tab.plan || !moduleId || courses[moduleId]) return;
    const plan = tab.plan;
    latestModule.current[plan] = moduleId;
    (async () => {
      busy(`c${moduleId}`, true);
      try {
        const res = await axios.get(`${API}/api/teacher/Curriculum/paid/${moduleId}/${plan}`);
        const list = (res.data?.courses || []).map((c) => ({
          id: c.Id,
          name: c.CourseName,
          image: c.CourseFilesLink,
          link: c.CourseLink,
          ppt: c.CourseProjectLink,
          note: c.CourseNote,
          assignment: c.CourseAssignmentLink,
        }));
        setCourses((prev) => ({ ...prev, [moduleId]: list }));
      } catch (err) {
        console.error('Error fetching courses:', err);
        if (latestModule.current[plan] === moduleId) setError('We couldn’t load the projects of this module.');
      } finally {
        busy(`c${moduleId}`, false);
      }
    })();
  }, [tab.plan, moduleId, courses, retry]);

  // ---------- Track "Check course" (same endpoint + same payload) ----------
  const trackOpen = (course) => {
    const moduleName = (modules[tab.plan] || []).find((m) => m.id === moduleId)?.name || 'Unknown Module';
    const openTime = new Date().toLocaleString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    axios
      .post(
        `${API}/api/teacher/Curriculum/track-course-open`,
        {
          teacherName: userData?.fullName || 'Unknown Teacher',
          teacherId: userData?.id || 'N/A',
          teacherEmail: userData?.email || 'N/A',
          courseName: course.name,
          moduleName,
          courseType: tab.type,
          openTime,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } }
      )
      .catch((err) => console.error('Error sending notification:', err));
  };

  const tryAgain = () => {
    setError('');
    setRetry((n) => n + 1);
  };

  /* ---------------- Render helpers ---------------- */
  const renderListSkeleton = () => (
    <div className="rmtcu-list" aria-busy="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="rmtcu-skel rmtcu-skel--row" />
      ))}
    </div>
  );

  const renderError = () => (
    <div className="rmtcu-empty">
      <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
      <p>{error}</p>
      <button type="button" className="rmtcu-btn" onClick={tryAgain}>
        Try again
      </button>
    </div>
  );

  const renderDemoGroup = (title, sub, list) => (
    <section className="rmtcu-group">
      <div className="rmtcu-group-head">
        <h2>{title}</h2>
        <span>{sub}</span>
        {list[0]?.ageRange && <em>{list[0].ageRange}</em>}
      </div>
      {list.length === 0 ? (
        <p className="rmtcu-none">No demo sessions yet.</p>
      ) : (
        <ul className="rmtcu-list">
          {list.map((d) => (
            <li key={d.id} className="rmtcu-row">
              <img className="rmtcu-thumb" src={d.image || DEFAULT_IMG} alt="" loading="lazy" onError={onImgError} />
              <div className="rmtcu-row-body">
                <h3>{d.name}</h3>
                {d.details && <p className="rmtcu-row-details">{d.details}</p>}
              </div>
              {d.link && (
                <div className="rmtcu-actions">
                  <a className="rmtcu-link is-main" href={d.link} target="_blank" rel="noopener noreferrer">
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> View demo
                  </a>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  const renderDemo = () => {
    if (error && !demo) return renderError();
    if (loading.demo || !demo) return renderListSkeleton();
    return (
      <>
        {renderDemoGroup('Little', 'Grade 1 – Grade 3', demo.little)}
        {renderDemoGroup('Junior', 'Grade 4 – Grade 12', demo.junior)}
        <ParentShowcase />
      </>
    );
  };

  const renderCourse = () => {
    const list = modules[tab.plan];
    if (error && (!list || !courses[moduleId])) return renderError();
    if (loading[`m${tab.plan}`] || !list) return renderListSkeleton();
    if (!list.length) {
      return (
        <div className="rmtcu-empty">
          <FontAwesomeIcon icon={faFolderOpen} />
          <p>No modules in this course yet.</p>
        </div>
      );
    }
    const items = courses[moduleId];
    const activeName = list.find((m) => m.id === moduleId)?.name;
    return (
      <>
        <div className="rmtcu-picker">
          <label htmlFor="rmtcu-module">Module</label>
          <div className="rmtcu-select">
            <select
              id="rmtcu-module"
              value={moduleId || ''}
              onChange={(e) => {
                setError('');
                setActiveModule((prev) => ({ ...prev, [tab.plan]: e.target.value }));
              }}
            >
              {list.map((m, i) => (
                <option key={m.id} value={m.id}>
                  {i + 1}. {m.name}
                </option>
              ))}
            </select>
            <FontAwesomeIcon icon={faChevronDown} aria-hidden="true" />
          </div>
          {items && <span className="rmtcu-count">{items.length} projects</span>}
        </div>

        {loading[`c${moduleId}`] || !items ? (
          renderListSkeleton()
        ) : items.length === 0 ? (
          <div className="rmtcu-empty">
            <FontAwesomeIcon icon={faFolderOpen} />
            <p>No projects in {activeName || 'this module'} yet.</p>
          </div>
        ) : (
          <ol className="rmtcu-list">
            {items.map((c, i) => (
              <li key={c.id} className="rmtcu-row">
                <img className="rmtcu-thumb" src={c.image || DEFAULT_IMG} alt="" loading="lazy" onError={onImgError} />
                <div className="rmtcu-row-body">
                  <span className="rmtcu-row-num">Project {i + 1}</span>
                  <h3>{(c.name || '').trim()}</h3>
                </div>
                <div className="rmtcu-actions">
                  {c.link && (
                    <a className="rmtcu-link" href={c.link} target="_blank" rel="noopener noreferrer">
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> View project
                    </a>
                  )}
                  {c.ppt && (
                    <a
                      className="rmtcu-link is-main"
                      href={c.ppt}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackOpen(c)}
                    >
                      <FontAwesomeIcon icon={faPersonChalkboard} /> Check course
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </>
    );
  };

  if (!userData) {
    return (
      <div className="rmtcu">
        <div className="rmtcu-inner">{renderListSkeleton()}</div>
      </div>
    );
  }

  return (
    <div className="rmtcu">
      <div className="rmtcu-inner">
        <header className="rmtcu-head">
          <span className="rmtcu-eyebrow">Curriculum</span>
          <h1 className="rmtcu-title">
            Teaching <span>resources</span>
          </h1>
        </header>

        <div className="rmtcu-tabs" role="tablist" aria-label="Curriculum sections">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tabKey === t.key}
              className={`rmtcu-tab ${tabKey === t.key ? 'is-on' : ''}`}
              onClick={() => setTab(t.key)}
            >
              <FontAwesomeIcon icon={t.icon} />
              <span className="rmtcu-tab-text">
                {t.label}
                {t.sub && <small>{t.sub}</small>}
              </span>
            </button>
          ))}
        </div>

        <div className="rmtcu-panel" role="tabpanel">
          {tabKey === 'demo' ? renderDemo() : renderCourse()}
        </div>
      </div>
    </div>
  );
};

export default Curriculum;